import React from 'react';
import {
  Typography,
  Paper,
  Box,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
} from '@mui/material';
import { Brightness4, Brightness7, Info } from '@mui/icons-material';
import { useThemeStore } from '../../../shared/stores/themeStore';
import { useDensityStore } from '../../../shared/stores/densityStore';
import { VersionInfo } from '@billda/shared-ui';

/**
 * General settings area: about/version and appearance (theme, density).
 */
export const GeneralArea: React.FC = () => {
  const { mode, toggleMode } = useThemeStore();
  const { density, setDensity } = useDensityStore();

  return (
    <>
      {/* About Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Info sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">About</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Application information and version details.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <VersionInfo />
      </Paper>

      {/* Appearance Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {mode === 'light' ? (
            <Brightness7 sx={{ mr: 1, color: 'primary.main' }} />
          ) : (
            <Brightness4 sx={{ mr: 1, color: 'primary.main' }} />
          )}
          <Typography variant="h6">Appearance</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize how Billda looks on your device.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleMode}
                  data-testid="theme-mode-switch"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Dark Mode
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mode === 'dark'
                      ? 'Dark mode is enabled'
                      : 'Switch to dark mode for reduced eye strain'}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={density === 'comfortable'}
                  onChange={e =>
                    setDensity(e.target.checked ? 'comfortable' : 'compact')
                  }
                  data-testid="density-toggle"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    Comfortable Display
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {density === 'comfortable'
                      ? 'Comfortable mode - larger text and spacing'
                      : 'Compact mode - smaller text, more content visible'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Stack>
      </Paper>
    </>
  );
};
